import * as React from 'react';
import { Checkbox, Dropdown, IDropdownOption, Stack } from '@fluentui/react';
import { AgencyModelPickerItem, JobModelConfiguration } from './shared';

const DEFAULT_MODEL_KEY = '__default-model__';
const DEFAULT_REASONING_EFFORT_KEY = '__default-reasoning-effort__';

export function AgencyModelConfigurationEditor(props: {
    value: JobModelConfiguration | null;
    modelOptions: AgencyModelPickerItem[];
    disabled?: boolean;
    hideModelLabel?: boolean;
    onChange: (value: JobModelConfiguration | null) => void;
}) {
    const { value, modelOptions, disabled, hideModelLabel, onChange } = props;

    const effectiveModelOptions = React.useMemo(() => {
        const currentModelId = value?.modelId?.trim();
        if (!currentModelId || modelOptions.some(option => option.id === currentModelId)) {
            return modelOptions;
        }

        return [
            {
                id: currentModelId,
                name: `${currentModelId} (not in AgencyModels)`,
                reasoningEffortOptions: value?.reasoningEffort ? [value.reasoningEffort] : [],
            },
            ...modelOptions,
        ];
    }, [modelOptions, value?.modelId, value?.reasoningEffort]);

    const selectedModel = React.useMemo(
        () => effectiveModelOptions.find(option => option.id === value?.modelId),
        [effectiveModelOptions, value?.modelId],
    );

    const modelDropdownOptions = React.useMemo<IDropdownOption[]>(
        () => [
            { key: DEFAULT_MODEL_KEY, text: 'Default' },
            ...effectiveModelOptions.map(option => ({
                key: option.id,
                text: option.name,
            })),
        ],
        [effectiveModelOptions],
    );

    const reasoningEffortOptions = React.useMemo<IDropdownOption[]>(
        () => [
            { key: DEFAULT_REASONING_EFFORT_KEY, text: 'Default' },
            ...(selectedModel?.reasoningEffortOptions ?? []).map(option => ({
                key: option,
                text: option,
            })),
        ],
        [selectedModel],
    );

    return (
        <Stack tokens={{ childrenGap: 12 }}>
            <Dropdown
                label={hideModelLabel ? undefined : 'Model'}
                options={modelDropdownOptions}
                selectedKey={value?.modelId ?? DEFAULT_MODEL_KEY}
                onChange={(_, option) => {
                    const nextKey = String(option?.key ?? DEFAULT_MODEL_KEY);
                    if (nextKey === DEFAULT_MODEL_KEY) {
                        onChange(null);
                        return;
                    }

                    const nextModel = effectiveModelOptions.find(candidate => candidate.id === nextKey);
                    if (!nextModel) {
                        onChange(null);
                        return;
                    }

                    const nextReasoningEffort = value?.modelId === nextModel.id
                        && value.reasoningEffort
                        && nextModel.reasoningEffortOptions.includes(value.reasoningEffort)
                        ? value.reasoningEffort
                        : null;

                    onChange({
                        modelId: nextModel.id,
                        reasoningEffort: nextReasoningEffort,
                        useLongContext: value?.modelId === nextModel.id ? !!value.useLongContext : false,
                    });
                }}
                disabled={disabled}
            />

            {selectedModel && (
                <>
                    {selectedModel.reasoningEffortOptions.length > 0 && (
                        <Dropdown
                            label="Reasoning effort"
                            options={reasoningEffortOptions}
                            selectedKey={value?.reasoningEffort ?? DEFAULT_REASONING_EFFORT_KEY}
                            onChange={(_, option) => {
                                if (!selectedModel) {
                                    return;
                                }

                                const nextReasoningEffort = String(option?.key ?? DEFAULT_REASONING_EFFORT_KEY);
                                onChange({
                                    modelId: selectedModel.id,
                                    reasoningEffort: nextReasoningEffort === DEFAULT_REASONING_EFFORT_KEY ? null : nextReasoningEffort,
                                    useLongContext: !!value?.useLongContext,
                                });
                            }}
                            disabled={disabled}
                        />
                    )}

                    <Checkbox
                        label="Long context"
                        checked={!!value?.useLongContext}
                        onChange={(_, checked) => {
                            if (!selectedModel) {
                                return;
                            }

                            onChange({
                                modelId: selectedModel.id,
                                reasoningEffort: value?.reasoningEffort ?? null,
                                useLongContext: !!checked,
                            });
                        }}
                        disabled={disabled}
                    />
                </>
            )}
        </Stack>
    );
}
